import { isEqual } from "lodash";

import type { MessagePathNode } from "@/shared/domain/topics";
import type { TopicData } from "@/shared/state/visualization";
import {
  DEFAULT_ENTITY_ID,
  DEFAULT_POINT_CLOUD_GRADIENT,
  type Entity,
  type EntityID,
  type PointCloud,
  type PointCloudColorMode,
  type PointCloudGradient,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  axesOf,
  DEFAULT_POINT_CLOUD_PRESET,
  type PointCloudAxes,
} from "./pointCloudPresets";

export const DEFAULT_POINT_CLOUD_COLOR = "#4b77b4";

/** The parsed values of the point cloud form's fields. */
export interface PointCloudInputs {
  /** The topic's `data` path, or nothing bound yet. */
  source: TopicData | undefined;
  axes: PointCloudAxes;
  color: string;
  colorMode: PointCloudColorMode;
  /**
   * The gradient's picks, kept whatever the color mode so switching away and
   * back finds them. An undefined end is left to the shown message.
   */
  gradient: Omit<PointCloudGradient, "min" | "max"> & {
    min: number | undefined;
    max: number | undefined;
  };
  /** Absent means the renderer's default size. */
  pointSize: number | undefined;
  stride: number;
}

/**
 * Validates a parsed point size. Returns an error message if invalid, or
 * undefined if valid.
 */
export function validatePointSize(value: number): string | undefined {
  if (value <= 0) {
    return "Point size must be greater than 0.";
  }
  return undefined;
}

/**
 * Validates a gradient's pinned range. Returns an error message if invalid, or
 * undefined if valid. An end left to the shown message cannot be checked here,
 * so only a range pinned at both ends is.
 */
export function validateGradientRange(
  min: number | undefined,
  max: number | undefined,
): string | undefined {
  if (min !== undefined && max !== undefined && max <= min) {
    return "Max must be greater than Min.";
  }
  return undefined;
}

/**
 * The inputs the point cloud form shows for a stored cloud: its own values,
 * with each one it leaves out at the default it stands for. With no cloud,
 * those of a new one, with no topic bound.
 *
 * `buildPointCloudSpec` of the result is the cloud as the form would write it
 * untouched. That equals a cloud the form wrote, but not one that spells out a
 * default the builder leaves out (`stride: 1`), so comparing against it rather
 * than the stored cloud is how the form tells a user's edit from a cloud that
 * was only opened.
 */
export function pointCloudInputsFor(
  cloud: PointCloud | undefined,
): PointCloudInputs {
  const gradient = cloud?.gradient ?? DEFAULT_POINT_CLOUD_GRADIENT;
  return {
    source: cloud?.source,
    axes: cloud
      ? { right: cloud.right, forward: cloud.forward, up: cloud.up }
      : axesOf(DEFAULT_POINT_CLOUD_PRESET),
    color: cloud?.color || DEFAULT_POINT_CLOUD_COLOR,
    colorMode: cloud?.colorMode ?? "auto",
    gradient: {
      by: gradient.by,
      colorLow: gradient.colorLow,
      colorHigh: gradient.colorHigh,
      min: gradient.min,
      max: gradient.max,
    },
    pointSize: cloud?.pointSize,
    stride: cloud?.stride ?? 1,
  };
}

/**
 * Builds a PointCloud spec from parsed inputs, or `null` while no topic is
 * bound (nothing to dispatch).
 *
 * Optional fields are left out — not set to `undefined` — when they match
 * their defaults, so the persisted shape stays minimal and equals what a
 * layout stores as JSON. The gradient is kept in every color mode unless it is
 * `DEFAULT_POINT_CLOUD_GRADIENT`, which is what the renderer draws without one.
 */
export function buildPointCloudSpec(
  inputs: PointCloudInputs,
): PointCloud | null {
  if (!inputs.source) {
    return null;
  }
  const { min, max, ...ends } = inputs.gradient;
  const gradient: PointCloudGradient = {
    ...ends,
    ...(min === undefined ? {} : { min }),
    ...(max === undefined ? {} : { max }),
  };
  return {
    source: inputs.source,
    right: inputs.axes.right,
    forward: inputs.axes.forward,
    up: inputs.axes.up,
    color: inputs.color,
    ...(inputs.colorMode === "auto" ? {} : { colorMode: inputs.colorMode }),
    ...(isEqual(gradient, DEFAULT_POINT_CLOUD_GRADIENT) ? {} : { gradient }),
    ...(inputs.pointSize === undefined ? {} : { pointSize: inputs.pointSize }),
    ...(inputs.stride === 1 ? {} : { stride: inputs.stride }),
  };
}

/**
 * The entity a point cloud topic becomes when it is sent to a 3D panel from
 * the topic tree: carrying the cloud the form would build for the topic's
 * `data` path and leave untouched — the default preset, flat in the default
 * color — and nothing else. The settings take it from there.
 *
 * Named `entityN` like every other entity: `entity0` for a new panel, as
 * `CreatePanel` names its first, and the panel's next free number
 * (`nextEntityId`) when it joins one that has entities already.
 *
 * `dataPath` is the topic's `data` leaf, as `isPointCloudDataPath` accepts it.
 */
export function pointCloudEntityFor(
  dataPath: MessagePathNode,
  id: EntityID = DEFAULT_ENTITY_ID,
): Entity {
  const pointCloud = buildPointCloudSpec({
    ...pointCloudInputsFor(undefined),
    source: dataPath.toTopicData(),
  });
  if (pointCloud === null) {
    // `buildPointCloudSpec` is null only with no source, and one was given.
    throw new Error("A bound source builds a point cloud");
  }
  return { id, pointCloud };
}
